import type { HistoryItem } from '@poseidon/components/ContactDetail';
import type { HistoryStepType } from '@poseidon/components/HistoryStep';
import { interactionHistoryAction } from '@poseidon/components/ContactDetail';
import { getInteractionStepType } from '@poseidon/components/HistoryStep';
import { stripHtmlTags } from '@poseidon/shared-utils/html';

/**
 * Only what a card reads off a history entry. Every `HistoryItem` fits it (it's a structural subset), so the
 * canvas passes its entries straight in.
 */
export interface HistoryCardEntry {
  interaction: Pick<
    HistoryItem['interaction'],
    'odooInteractionAction' | 'interactionType' | 'userId' | 'createdAt' | 'subject' | 'body'
  >;
  interactionUser: { name: string | null } | null;
  contact: { primaryFields: Partial<Record<string, { data: string }>> } | null;
  documentView: { title: string } | null;
  cadence: { name: string | null } | null;
}

/** One history entry as a canvas card says it: its icon kind, a one-line "who did what", and a text glimpse. */
export interface HistoryCardSummary {
  stepType: HistoryStepType;
  title: string;
  /** The first words of what was said, for an entry that carries text (a message, a reply, an email). */
  snippet: string | null;
  at: Date;
}

const SNIPPET_CHARS = 90;

/** Plain, single-line, length-capped text from a stored HTML / text body. */
function glimpse(html: string | null | undefined): string | null {
  const text = stripHtmlTags(html).replace(/\s+/g, ' ').trim();
  if (text === '') return null;
  return text.length > SNIPPET_CHARS ? `${text.slice(0, SNIPPET_CHARS - 1).trimEnd()}…` : text;
}

/**
 * The compact card text for a history entry on the sequence canvas. The full entry — Respond, the transcript,
 * the document — opens from the card; this is only enough to recognise it while panning.
 */
export function summarizeHistoryEntry(entry: HistoryCardEntry, currentUserId: string): HistoryCardSummary {
  const action = interactionHistoryAction(entry);
  const i = entry.interaction;
  const who = i.userId === currentUserId ? 'You' : (entry.interactionUser?.name ?? 'A teammate');
  const them = entry.contact?.primaryFields?.firstName?.data ?? 'They';
  const campaign = entry.cadence?.name ?? 'a campaign';
  const titles: Record<string, string> = {
    message_sent: `${who} sent a message`,
    message_received: `${them} replied`,
    inmail_sent: `${who} sent an InMail`,
    inmail_received: `${them} replied to an InMail`,
    inmail_declined: `${them} declined an InMail`,
    email_sent: `${who} sent an email`,
    email_received: `${them} replied by email`,
    connection_sent: `${who} sent a connection request`,
    connection_accepted: `${them} accepted the connection`,
    phone_call_sent: `${who} called`,
    phone_received: `${them} called`,
    cadence_start: `Started ${campaign}`,
    campaign_start: `Started ${campaign}`,
    campaign_delete: `Removed from ${campaign}`,
    document_viewed: `${them} viewed ${entry.documentView?.title ?? 'a document'}`,
    comment_on_post: `${who} commented on a post`,
    stage_changed: i.subject != null && i.subject !== '' ? `Moved to ${i.subject}` : 'Stage changed',
    visit: `${who} visited their profile`,
    follow: `${who} followed them`,
    like_posts: `${who} liked their posts`,
    reminder_completed: 'Reminder done',
    step_failed: 'A step failed',
    step_skipped: 'A step was skipped',
    step_canceled: 'A step was canceled',
    meeting_booked: 'Meeting booked',
    interest_detected: 'Interest detected',
    contact_unavailable: 'Contact unavailable',
    contact_available: 'Contact available again',
    cadence_state_changed: i.subject != null && i.subject !== '' ? i.subject : 'Campaign state changed',
  };
  const carriesText =
    action === 'message_sent' ||
    action === 'message_received' ||
    action === 'inmail_sent' ||
    action === 'inmail_received' ||
    action === 'email_sent' ||
    action === 'email_received' ||
    action === 'comment_on_post';
  return {
    stepType: getInteractionStepType(action),
    title: titles[action] ?? action.replace(/_/g, ' '),
    snippet: carriesText ? (glimpse(i.body) ?? glimpse(i.subject)) : null,
    at: i.createdAt,
  };
}
